import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth, useClient } from "../context/auth-context";
import { useTenantContext } from "../context/tenant-context";
import { useToast } from "../context/toast-context";
import { AuthFlow } from "../domain/auth";
import {
  ConfigurationLevel,
  FeatureConfiguration,
  FeatureConfigurationPatchPayload,
  FeatureRuleTemplate
} from "../domain/features";
import { client as pureClient } from "../utils/api-client";

export const defaultConfiguration: FeatureConfiguration = {
  activeServices: {
    paths: true,
    worlds: false
  },
  canViewFollowers: false,
  reactions: [],
  interests: [],
  kudosCategories: [],
  enableMultipleOrganisations: false,
  surveyMoodQuestionOptions: [],
  reelConfiguration: {
    requireReviewBeforePublishing: false
  },
  appConfiguration: {
    isSocialEnabled: false,
    isKudosEnabled: false,
    isChatEnabled: false,
    isSurveysEnabled: false,
    isCoursesEnabled: false,
    isCompanyGoalsEnabled: false,
    isCompanyValuesEnabled: false,
    isContentZoneEnabled: false,
    isMHFAEnabled: false,
    isGroupsEnabled: false,
    isUserPostAndCommentEnabled: false,
    isCalendarEnabled: false,
    maxNumberOfMediaInComments: 0,
    maxPostLength: 0
  },
  allowPostToAncestorAndDescendantOrganisations: false,
  allowUserPostToDepartment: false,
  includeStructureOptionsInGroupAudienceList: false,
  disableSendingInvitations: false,
  hashTagRegex: { pattern: "", flags: "" },
  urlRegex: { pattern: "", flags: "" },
  flags: {
    isSocialEnabled: false,
    isKudosEnabled: false,
    isContentZoneEnabled: false,
    isBadgesEnabled: false,
    isWebGameComponentEnabled: false,
    allowDangerousIntegrationStructureChanges: false
  },
  authConfig: {
    allowedPhoneCountryCodes: [],
    authFlow: AuthFlow.passwordless
  },
  emailRegex: {
    flags: "",
    pattern: ""
  },
  defaultUserRole: "user",
  apiVersion: 1,
  intelligentSearch: {
    defaultScore: 0.2,
    maxResults: 10,
    generateAnswer: false,
    indexOnlyVerified: false,
    translateQuestionsFromOtherLanguages: false,
    indexedContentLanguage: "English",
    answerContext: {
      maxNumberOfWords: 2000,
      maxResultsToUse: 3,
      maxFieldsPerResult: 3,
      providers: []
    },
    modelSettings: {
      embeddingModel: "",
      useChatCompletion: true,
      completionModel: "",
      chatCompletionModel: "",
      temperature: 0.1,
      promptFormat: "Context: {{context}}\n===\nQ: {{query}}\nA:",
      chatPromptPrefix: "Using the following context, answer the question\n{{prompt}}"
    }
  },
  maxNumberOfIntegrations: 0,
  compliance: {
    successMinValue: 0.5,
    warnMinValue: 0.2,
    soonThresholdDays: 7
  }
};

function useFeaturesQuery() {
  const client = useClient<FeatureConfiguration>();
  const { activeTenant } = useTenantContext();
  const { data: featuresConfig, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!activeTenant,
    queryKey: `tenant-${activeTenant}-features`,
    queryFn: () =>
      client(`tenants/${activeTenant}/features`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });
  return { featuresConfig: featuresConfig ?? defaultConfiguration, isLoading };
}

interface TenantFeatureConfiguration extends FeatureConfiguration {
  _tenantTemplateId?: string;
}

function useFeatureConfigurationForTenant(tenantId?: string) {
  const client = useClient<TenantFeatureConfiguration>();
  const { data: { _tenantTemplateId: templateId, ...featuresConfig } = {}, isLoading } = useQuery<
    TenantFeatureConfiguration,
    Error
  >({
    enabled: !!tenantId,
    queryKey: ["featureconfiguration", tenantId],
    queryFn: () =>
      client(`features?level=tenant&tenantId=${tenantId}`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });

  return { featuresConfig: (featuresConfig as FeatureConfiguration) ?? defaultConfiguration, isLoading, templateId };
}

function useFeatureConfigurationForOrganisation(tenantId?: string, organisationId?: string) {
  const client = useClient<FeatureConfiguration>();
  const { data: featuresConfig, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!tenantId && !!organisationId,
    queryKey: ["featureconfiguration-org", tenantId, organisationId],
    queryFn: () =>
      client(`features?level=organisation&tenantId=${tenantId}&organisationId=${organisationId}`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });
  return { featuresConfig: featuresConfig ?? defaultConfiguration, isLoading };
}

function useFeatureConfigurationForDepartment(tenantId?: string, organisationId?: string, departmentId?: string) {
  const client = useClient<FeatureConfiguration>();
  const { data: featuresConfig, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!tenantId && !!organisationId && !!departmentId,
    queryKey: ["featureconfiguration-department", tenantId, organisationId, departmentId],
    queryFn: () =>
      client(
        `features?level=department&tenantId=${tenantId}&organisationId=${organisationId}&departmentId=${departmentId}`
      )
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });
  return { featuresConfig: featuresConfig ?? defaultConfiguration, isLoading };
}

function useFeatureConfigurationForRole(tenantId?: string, role?: string) {
  const client = useClient<FeatureConfiguration>();
  const { data: featuresConfig, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!tenantId && !!role,
    queryKey: ["featureconfiguration-role", tenantId, role],
    queryFn: () =>
      client(`features?level=role&tenantId=${tenantId}&role=${role}`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });
  return { featuresConfig: featuresConfig ?? defaultConfiguration, isLoading };
}

function useFeatureConfigurationForPlatformGroup(platformGroupId?: string) {
  const client = useClient<FeatureConfiguration>();
  const { data: featuresConfig, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!platformGroupId,
    queryKey: ["featureconfiguration-platformGroup", platformGroupId],
    queryFn: () =>
      client(`features?level=platformGroup&platformGroupId=${platformGroupId}`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });
  return { featuresConfig: featuresConfig ?? defaultConfiguration, isLoading };
}

interface CompactRuleResult {
  level: ConfigurationLevel;
  appliesTo: string;
  dryRun: boolean;
  removed: string[];
  originalSettings: Partial<FeatureConfiguration>;
  remainingSettings: Partial<FeatureConfiguration>;
}

function useGetRedundantConfiguration(
  level: ConfigurationLevel,
  tenantId: string | undefined,
  organisationId: string | undefined,
  departmentId: string | undefined,
  role: string | undefined,
  platformGroupId: string | undefined
) {
  const client = useClient<CompactRuleResult>();
  const queryParams = new URLSearchParams();
  queryParams.append("level", level);
  if (tenantId) {
    queryParams.append("tenantId", tenantId);
  }
  if (organisationId) {
    queryParams.append("organisationId", organisationId);
  }
  if (departmentId) {
    queryParams.append("departmentId", departmentId);
  }
  if (role) {
    queryParams.append("role", role);
  }
  if (platformGroupId) {
    queryParams.append("platformGroupId", platformGroupId);
  }

  const query = queryParams.toString();

  const { data, isLoading, refetch } = useQuery<CompactRuleResult, Error>({
    queryKey: ["redundant-feature-config", query],
    queryFn: () =>
      client(`features/compact?${query}`)
        .then(data => data)
        .catch(error => error)
  });

  return {
    result: data ?? {
      level,
      appliesTo: "",
      dryRun: true,
      removed: [],
      originalSettings: {},
      remainingSettings: {}
    },
    isLoading,
    refetch
  };
}

function useRemoveRedundantConfiguration() {
  const client = useClient();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<
    unknown,
    Error,
    {
      level: ConfigurationLevel;
      tenantId: string | undefined;
      organisationId: string | undefined;
      departmentId: string | undefined;
      role: string | undefined;
      platformGroupId: string | undefined;
    }
  >(
    ({ level, tenantId, organisationId, departmentId, role, platformGroupId }) => {
      const queryParams = new URLSearchParams();
      queryParams.append("level", level);
      if (tenantId) {
        queryParams.append("tenantId", tenantId);
      }
      if (organisationId) {
        queryParams.append("organisationId", organisationId);
      }
      if (departmentId) {
        queryParams.append("departmentId", departmentId);
      }
      if (role) {
        queryParams.append("role", role);
      }
      if (platformGroupId) {
        queryParams.append("platformGroupId", platformGroupId);
      }
      return client(`features/compact?${queryParams.toString()}`, {
        method: "POST",
        data: {}
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["featureconfiguration"]);
        queryClient.invalidateQueries(["featureconfiguration-org"]);
        queryClient.invalidateQueries(["featureconfiguration-platformGroup"]);

        showSnackBar && showSnackBar("Redundant configuration removed", undefined, "success");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error removing redundant configuration", undefined, "error");
      }
    }
  );

  return mutation;
}

function useDefaultFeatureConfiguration() {
  const client = useClient<FeatureConfiguration>();
  const { data: featuresConfig, isLoading } = useQuery<FeatureConfiguration, Error>({
    queryKey: ["featureconfiguration", "__DEFAULT__"],
    queryFn: () =>
      client(`features?level=global`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });
  return { featuresConfig: featuresConfig ?? defaultConfiguration, isLoading };
}

interface AddFeatureOverrideCommand {
  level: ConfigurationLevel;
  appliesTo?: string;
  settings: Partial<FeatureConfiguration>;
  onSuccess?: () => void;
}

function useApplyFeatureOverride() {
  const client = useClient();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<unknown, Error, AddFeatureOverrideCommand>(
    command => {
      const { onSuccess, ...data } = command;
      return client(`features`, {
        method: "PATCH",
        data
      });
    },
    {
      onSuccess: (_, command) => {
        queryClient.invalidateQueries(["featureconfiguration"]);
        if (command.appliesTo) {
          queryClient.invalidateQueries(`tenant-${command.appliesTo}-features`);
        }

        command.onSuccess && command.onSuccess();

        showSnackBar && showSnackBar("Changes applied");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error applying changes", undefined, "error");
      }
    }
  );

  return mutation;
}

interface ApplyTemplateForTenantCommand {
  tenantId: string;
  templateId: string;
}

function useApplyFeatureTemplateForTenant() {
  const client = useClient();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<unknown, Error, ApplyTemplateForTenantCommand>(
    command => {
      return client(`features`, {
        method: "PATCH",
        data: {
          level: "tenant",
          appliesTo: command.tenantId,
          templateId: command.templateId,
          settings: {}
        }
      });
    },
    {
      onSuccess: (_, command) => {
        queryClient.invalidateQueries(["featureconfiguration"]);
        queryClient.invalidateQueries(`tenant-${command.tenantId}-features`);

        showSnackBar && showSnackBar("Template applied", undefined, "success");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error applying template", undefined, "error");
      }
    }
  );

  return mutation;
}

function useRemoveFeatureTemplateFromTenant() {
  const client = useClient();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<unknown, Error, { tenantId: string }>(
    ({ tenantId }) => {
      return client(`features/${tenantId}/template`, {
        method: "DELETE"
      });
    },
    {
      onSuccess: (_, { tenantId }) => {
        queryClient.invalidateQueries(["featureconfiguration"]);
        queryClient.invalidateQueries(["featureconfiguration-org"]);
        queryClient.invalidateQueries(["featureconfiguration-platformGroup"]);
        queryClient.invalidateQueries(`tenant-${tenantId}-features`);

        showSnackBar && showSnackBar("Template removed", undefined, "success");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error removing template", undefined, "error");
      }
    }
  );

  return mutation;
}

interface ResetTenantToDefaultConfigurationCommand {
  tenantId: string;
  onSuccess?: () => void;
}

function useResetTenantConfigurationToDefault() {
  const client = useClient();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<unknown, Error, ResetTenantToDefaultConfigurationCommand>(
    command => {
      return client(`features?level=tenant&appliesTo=${command.tenantId}`, {
        method: "DELETE"
      });
    },
    {
      onSuccess: (_, command) => {
        queryClient.invalidateQueries(["featureconfiguration"]);
        queryClient.invalidateQueries(["featureconfiguration-org"]);
        queryClient.invalidateQueries(["featureconfiguration-platformGroup"]);

        if (command.tenantId) {
          queryClient.invalidateQueries(`tenant-${command.tenantId}-features`);
        }

        command.onSuccess && command.onSuccess();

        showSnackBar && showSnackBar("Changes applied");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error applying changes", undefined, "error");
      }
    }
  );

  return mutation;
}

function useTenantFeaturesWithoutAppClient(token?: string) {
  const { activeTenant } = useTenantContext();
  const { features: publicFeatures } = usePublicFeatures();

  const { data, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!activeTenant && !!token,
    queryKey: [`auth-configs-pure`, activeTenant],
    queryFn: () =>
      pureClient<FeatureConfiguration>(`tenants/${activeTenant}/features`, { token })
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return publicFeatures;
    }
  });

  return { features: data ?? publicFeatures, isLoading };
}

const useApiVersion = () => {
  const { identity } = useAuth();
  const { features } = useTenantFeaturesWithoutAppClient(identity?.token);
  return { apiVersion: features.apiVersion };
};

const usePatchFeatures = () => {
  const client = useClient<FeatureConfiguration>();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();
  const { activeTenant } = useTenantContext();

  const mutation = useMutation<FeatureConfiguration, Error, FeatureConfigurationPatchPayload>(
    updates =>
      client(`tenants/${activeTenant}/features`, {
        method: "PATCH",
        data: updates
      }),
    {
      onSuccess: (_, updates) => {
        queryClient.setQueryData(`tenant-${activeTenant}-features`, (old: FeatureConfiguration | undefined) => {
          if (old) {
            return { ...old, appConfiguration: { ...old.appConfiguration, ...updates } };
          } else {
            return defaultConfiguration;
          }
        });

        showSnackBar && showSnackBar("featureSwitched");
      },
      onError: _ => {
        showSnackBar && showSnackBar("featureSwitchedError");
      }
    }
  );

  return mutation;
};

function usePublicFeatures() {
  const { data, isLoading } = useQuery<FeatureConfiguration, Error>({
    queryKey: [`auth-config`],
    queryFn: () =>
      pureClient<FeatureConfiguration>(`features/public`)
        .then(data => data)
        .catch(error => error),
    onError: _ => {
      return defaultConfiguration;
    }
  });

  return { features: data ?? defaultConfiguration, isLoading };
}

function useQueryFeatureRuleTemplates() {
  const client = useClient<FeatureRuleTemplate[]>();

  const { data, isLoading } = useQuery<FeatureRuleTemplate[], Error>({
    queryKey: [`feature-rule-templates`],
    queryFn: () => client(`features/templates`)
  });

  return { featureRuleTemplates: data ?? [], isLoading };
}

function useCreateFeatureRuleTemplate() {
  const client = useClient<FeatureRuleTemplate>();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<
    FeatureRuleTemplate,
    Error,
    {
      name: string;
      description: string;
      settings: Partial<FeatureConfiguration>;
    }
  >(
    ({ name, description, settings }) => {
      return client(`features/templates`, {
        method: "POST",
        data: {
          name,
          description,
          settings
        }
      });
    },
    {
      onSuccess: created => {
        queryClient.setQueryData([`feature-rule-templates`], (old: FeatureRuleTemplate[] | undefined) => {
          return [...((old as FeatureRuleTemplate[]) ?? []), created] as FeatureRuleTemplate[];
        });
        showSnackBar && showSnackBar("Template created", undefined, "success");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error creating template", undefined, "error");
      }
    }
  );

  return mutation;
}

function usePatchFeatureRuleTemplate() {
  const client = useClient<FeatureRuleTemplate>();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<
    FeatureRuleTemplate,
    Error,
    {
      id: string;
      name?: string;
      description?: string;
      settings?: Partial<FeatureConfiguration>;
    }
  >(
    ({ id, name, description, settings }) => {
      return client(`features/templates/${id}`, {
        method: "PATCH",
        data: {
          name,
          description,
          settings
        }
      });
    },
    {
      onSuccess: updated => {
        queryClient.setQueryData([`feature-rule-templates`], (old: FeatureRuleTemplate[] | undefined) => {
          return [
            ...((old as FeatureRuleTemplate[]) ?? []).map(t => {
              if (t.id === updated.id) {
                return updated;
              }
              return t;
            })
          ] as FeatureRuleTemplate[];
        });
        queryClient.invalidateQueries([`featureconfiguration-template-effective-configuration`]);
        showSnackBar && showSnackBar("Template updated", undefined, "success");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error updating template", undefined, "error");
      }
    }
  );

  return mutation;
}

function useGetEffectiveConfigurationForTemplate(templateId?: string) {
  const client = useClient<FeatureConfiguration>();

  const { data, isLoading } = useQuery<FeatureConfiguration, Error>({
    enabled: !!templateId,
    queryKey: [`featureconfiguration-template-effective-configuration`, templateId],
    queryFn: () => client(`features/templates/${templateId}/effectiveconfiguration`),
    onError: _ => {
      return defaultConfiguration;
    }
  });

  return { featuresConfig: data ?? defaultConfiguration, isLoading };
}

function useDeleteFeatureRuleTemplate() {
  const client = useClient();
  const queryClient = useQueryClient();
  const { showSnackBar } = useToast();

  const mutation = useMutation<unknown, Error, { id: string }>(
    ({ id }) => {
      return client(`features/templates/${id}`, {
        method: "DELETE"
      });
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.setQueryData([`feature-rule-templates`], (old: FeatureRuleTemplate[] | undefined) => {
          return [...((old as FeatureRuleTemplate[]) ?? []).filter(t => t.id !== id)] as FeatureRuleTemplate[];
        });

        showSnackBar && showSnackBar("Template deleted", undefined, "success");
      },
      onError: () => {
        showSnackBar && showSnackBar("Error deleting template", undefined, "error");
      }
    }
  );

  return mutation;
}

export {
  useApiVersion,
  useApplyFeatureOverride,
  useApplyFeatureTemplateForTenant,
  useCreateFeatureRuleTemplate,
  useDefaultFeatureConfiguration,
  useDeleteFeatureRuleTemplate,
  useFeatureConfigurationForDepartment,
  useFeatureConfigurationForOrganisation,
  useFeatureConfigurationForPlatformGroup,
  useFeatureConfigurationForRole,
  useFeatureConfigurationForTenant,
  useFeaturesQuery,
  useGetEffectiveConfigurationForTemplate,
  useGetRedundantConfiguration,
  usePatchFeatureRuleTemplate,
  usePatchFeatures,
  usePublicFeatures,
  useQueryFeatureRuleTemplates,
  useRemoveFeatureTemplateFromTenant,
  useRemoveRedundantConfiguration,
  useResetTenantConfigurationToDefault,
  useTenantFeaturesWithoutAppClient
};
